/**
 * The skills library (B2): Company-level, Bayes Price, My skills (drafts
 * first) and My colleagues' skills, in the TierSection look Data Viz and the
 * personas library use. Drawn by the agent panel's Skills tab and by Admin →
 * Skills.
 *
 * Every action is drawn on every row; one the viewer cannot use is disabled
 * with the reason (utils/permissionReason.ts). The server enforces the same
 * rules per row. Platform skills are greyed with a "Built in" label: every
 * chat already has them, so they have no Install action, and they are only
 * copyable.
 *
 * A Bayes Price skill is Bayes Price's own, mounted read-only in every
 * customer's chats (#2021). A customer's users read and Duplicate it; only a
 * Bayes Price Manager edits, deletes, moves skills to it and turns it off for
 * a customer. One turned off for this customer is drawn greyed with "Off for
 * your organisation"; one hidden by a skill of the same name from the
 * customer says which. A duplicate whose original has moved on says so.
 */
import React, { useState } from 'react'
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment,
  List, ListItem, TextField, Tooltip, Typography,
} from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import PublicIcon from '@mui/icons-material/Public'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import SearchIcon from '@mui/icons-material/Search'
import TierSection, { type TierVariant } from '../dashboard/TierSection'
import MenuActionsButton, { type MenuAction } from '../system/MenuActionsButton'
import DisabledReason from '../system/DisabledReason'
import { refuse, type WhoToAsk } from '../../utils/permissionReason'
import type { SkillDuplicateLevel, SkillInfo } from '../../types/agent'

export interface SkillsLibraryProps {
  /** The library's customer: Managers turn Bayes Price skills on and off for it. */
  customer: string
  company: SkillInfo[]
  bayesprice: SkillInfo[]
  mine: SkillInfo[]
  drafts: SkillInfo[]
  colleagues: SkillInfo[]
  loading: boolean
  currentUserEmail: string
  /** A Manager (the library's can_promote). */
  canPromote: boolean
  /** Bayes Price Manager (the library's can_manage_bayesprice). */
  canManageBayesPrice: boolean
  whoToAsk: WhoToAsk
  /** Skills a person made that the session has installed; with onInstall, their rows offer Install. */
  installedNames?: Set<string>
  onInstall?: (skill: SkillInfo) => void
  onView: (skill: SkillInfo) => void
  onEdit: (skill: SkillInfo) => void
  onCopy: (skill: SkillInfo) => void
  onDelete: (skill: SkillInfo) => Promise<unknown>
  onPromote: (skill: SkillInfo) => Promise<unknown>
  onDemote: (skill: SkillInfo) => Promise<unknown>
  /** Save one of your drafts to My skills in one click, from its row (Matt, 28 Oct). */
  onSaveDraft?: (skill: SkillInfo) => Promise<unknown>
  /** A copy of a Bayes Price skill, same name, in My skills or Company-level. */
  onDuplicate: (skill: SkillInfo, level: SkillDuplicateLevel) => Promise<unknown>
  /** Bayes Price Manager: move a skill to Bayes Price skills. */
  onMoveToBayesPrice: (skill: SkillInfo) => Promise<unknown>
  /** Bayes Price Manager: turn a Bayes Price skill off (or back on) for this customer. */
  onSetOff: (skill: SkillInfo, off: boolean) => Promise<unknown>
  /** Narrow (the side panel): smaller type, no search field padding. */
  dense?: boolean
}

const sameEmail = (a: string | null | undefined, b: string) => (a ?? '').trim().toLowerCase() === b.trim().toLowerCase()
export const isPlatformSkill = (s: SkillInfo) => Boolean(s.platform) || s.id.startsWith('core:')
/** Built in: every chat already has it, so it is never installed. Every platform skill is. */
export const isBuiltinSkill = (s: SkillInfo) => Boolean(s.builtin) || isPlatformSkill(s)
export const BUILTIN_REASON = 'Built in: every chat already has this skill.'
const isDraft = (s: SkillInfo) => s.status === 'draft'
/** Bayes Price's own: a public row that is not a built-in skill. */
export const isBayesPriceSkill = (s: SkillInfo) => s.visibility === 'public' && !isBuiltinSkill(s)

export const OFF_LABEL = 'Off for your organisation'
export const NEWER_LABEL = 'Bayes Price has a newer version'
export const SHADOWED_LABEL = { company: 'Your Company-level skill of this name is used instead', yours: 'Your skill of this name is used instead' }
const BAYESPRICE_READ_ONLY = 'Only Bayes Price can change this skill.'
export const OFF_DUPLICATE_REASON = 'This Bayes Price skill is turned off for your organisation.'
export const BAYESPRICE_MAKER = 'Bayes Price'

/**
 * Who made a skill, for display. A customer's people get a Bayes Price row with
 * the owner blank, so it reads "Bayes Price"; a Manager's row keeps the owner.
 */
export const makerOf = (s: SkillInfo) => s.owner_email || (isBayesPriceSkill(s) ? BAYESPRICE_MAKER : '')

/** When a stored version was saved. The server sends Unix seconds or milliseconds. */
export function formatSavedAt(at: number): string {
  const ms = at < 1e11 ? at * 1000 : at
  return new Date(ms).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
}

/** Why a row's action is disabled, or undefined when the viewer may use it. */
export function skillActionReasons(skill: SkillInfo, ctx: {
  currentUserEmail: string
  canPromote: boolean
  canManageBayesPrice: boolean
  whoToAsk: WhoToAsk
  installed: boolean
}) {
  const own = skill.visibility === 'private' && sameEmail(skill.owner_email, ctx.currentUserEmail)
  const platform = isPlatformSkill(skill)
  const colleague = skill.visibility === 'private' && !own
  const bayesPrice = isBayesPriceSkill(skill)
  const bayesPriceEditable = bayesPrice && ctx.canManageBayesPrice
  const companyEditable = skill.visibility === 'organizational' && ctx.canPromote

  const edit = (() => {
    if (own || companyEditable || bayesPriceEditable) return undefined
    if (platform) return "You can't edit a platform skill. Use Copy to make your own copy."
    if (bayesPrice) return `${BAYESPRICE_READ_ONLY} Use Duplicate to make your own copy.`
    if (colleague) return refuse("You can't edit a colleague's skill.", ctx.whoToAsk, { owner: skill.owner_email, copy: 'Copy' }).reason
    return refuse("You can't edit a Company-level skill.", ctx.whoToAsk, { copy: 'Copy' }).reason
  })()

  const remove = (() => {
    if (own || companyEditable || bayesPriceEditable) return undefined
    if (platform) return "You can't delete a platform skill."
    if (bayesPrice) return BAYESPRICE_READ_ONLY
    if (colleague) return refuse("You can't delete a colleague's skill.", ctx.whoToAsk, { owner: skill.owner_email }).reason
    return refuse("You can't delete a Company-level skill.", ctx.whoToAsk).reason
  })()

  const install = (() => {
    if (isBuiltinSkill(skill)) return BUILTIN_REASON
    if (bayesPrice) {
      if (skill.off_for_customer) return `${OFF_LABEL}: its chats do not have this skill.`
      if (skill.shadowed_by) return `${SHADOWED_LABEL[skill.shadowed_by]}.`
      return 'Bayes Price skills are already in every chat that starts after they are saved.'
    }
    if (ctx.installed) return 'Already installed in this session.'
    if (isDraft(skill)) return 'A draft is not installed in chats. Use Run a test chat in the editor to try it.'
    if (colleague) return refuse("You can't install a colleague's skill.", ctx.whoToAsk, { owner: skill.owner_email, copy: 'Copy' }).reason
    return undefined
  })()

  const promote = (() => {
    if (!ctx.canPromote) return refuse("You can't make a skill Company-level.", ctx.whoToAsk).reason
    if (isDraft(skill)) return 'Save the draft to My skills before making it Company-level.'
    return undefined
  })()

  // Duplicate keeps the name, so a Company-level copy replaces the original for everyone in the organisation: a Manager's call.
  // A skill that is off for the customer cannot be duplicated at all (the server answers 409).
  const duplicateOff = bayesPrice && skill.off_for_customer ? OFF_DUPLICATE_REASON : undefined
  const duplicateToCompany = duplicateOff ?? (ctx.canPromote ? undefined : refuse("You can't duplicate a skill to Company-level.", ctx.whoToAsk).reason)

  // The move changes who sees the skill, so a draft is saved first. A built-in skill cannot move: it would never mount.
  const moveToBayesPrice = isDraft(skill) ? 'Save the draft to My skills before moving it to Bayes Price skills.' : undefined

  // Only the owner may run a test chat (recordSkillTest), Company-level or not.
  const test = sameEmail(skill.owner_email, ctx.currentUserEmail)
    ? undefined
    : bayesPrice && !skill.owner_email
    ? `${BAYESPRICE_READ_ONLY} Use Duplicate to make your own copy.`
    : refuse("You can't run a test chat on a colleague's skill.", ctx.whoToAsk, { owner: skill.owner_email, copy: 'Copy' }).reason

  return { edit, remove, install, promote, test, duplicateOff, duplicateToCompany, moveToBayesPrice }
}

interface Tier {
  variant: TierVariant
  title: string
  emptyLabel: string
  skills: SkillInfo[]
}

const SkillsLibrary: React.FC<SkillsLibraryProps> = ({
  customer, company, bayesprice, mine, drafts, colleagues, loading, currentUserEmail, canPromote, canManageBayesPrice, whoToAsk,
  installedNames, onInstall, onView, onEdit, onCopy, onDelete, onPromote, onDemote, onSaveDraft, onDuplicate, onMoveToBayesPrice, onSetOff, dense,
}) => {
  const [openTiers, setOpenTiers] = useState<Partial<Record<TierVariant, boolean>>>({})
  const [query, setQuery] = useState('')
  const [deleting, setDeleting] = useState<SkillInfo | null>(null)
  const [demoting, setDemoting] = useState<SkillInfo | null>(null)
  const [moving, setMoving] = useState<SkillInfo | null>(null)
  const [savingId, setSavingId] = useState('')

  const handleSaveDraft = async (s: SkillInfo) => {
    if (!onSaveDraft) return
    setSavingId(s.id)
    try {
      await onSaveDraft(s)
    } catch {
      // The caller's mutation reports the failure; the draft stays a draft.
    } finally {
      setSavingId('')
    }
  }

  const q = query.trim().toLowerCase()
  const matches = (s: SkillInfo) => !q || `${s.name} ${s.description}`.toLowerCase().includes(q)
  const tiers: Tier[] = [
    { variant: 'company', title: 'Company-level', emptyLabel: 'No company-level skills yet', skills: company.filter(matches) },
    { variant: 'bayesprice', title: 'Bayes Price', emptyLabel: 'No Bayes Price skills yet', skills: bayesprice.filter(matches) },
    { variant: 'mine', title: 'My skills', emptyLabel: "You haven't made any skills yet", skills: [...drafts, ...mine].filter(matches) },
    { variant: 'colleagues', title: "My colleagues' skills", emptyLabel: 'No colleagues have skills yet', skills: colleagues.filter(matches) },
  ]
  // As in Data Viz, a tier starts open when it has rows, until toggled.
  const isOpen = (tier: Tier) => openTiers[tier.variant] ?? tier.skills.length > 0
  const toggle = (tier: Tier) => setOpenTiers({ ...openTiers, [tier.variant]: !isOpen(tier) })

  const isInstalled = (s: SkillInfo) => Boolean(s.installed) || Boolean(installedNames?.has(s.name))

  const confirmDelete = async () => {
    if (!deleting) return
    await onDelete(deleting)
    setDeleting(null)
  }
  const confirmDemote = async () => {
    if (!demoting) return
    await onDemote(demoting)
    setDemoting(null)
  }

  const confirmMove = async () => {
    if (!moving) return
    await onMoveToBayesPrice(moving)
    setMoving(null)
  }

  const renderStar = (s: SkillInfo, reason: string | undefined) => {
    if (isPlatformSkill(s) || isBayesPriceSkill(s)) return null
    const company = s.visibility === 'organizational'
    const label = company ? `Remove ${s.name} from Company-level` : `Make ${s.name} Company-level`
    const button = (
      <IconButton size="small" aria-label={label} onClick={() => (company ? setDemoting(s) : onPromote(s))}>
        {company ? <StarIcon fontSize="small" sx={{ color: 'warning.main' }} /> : <StarBorderIcon fontSize="small" />}
      </IconButton>
    )
    // Removing from Company-level needs the same right as adding.
    return reason ? <DisabledReason reason={reason}>{button}</DisabledReason> : <Tooltip title={company ? 'Remove from Company-level' : 'Make Company-level: the agent uses it for everyone'}>{button}</Tooltip>
  }

  const renderRow = (s: SkillInfo) => {
    const installed = isInstalled(s)
    const reasons = skillActionReasons(s, { currentUserEmail, canPromote, canManageBayesPrice, whoToAsk, installed })
    const platform = isPlatformSkill(s)
    const builtin = isBuiltinSkill(s)
    const bayesPrice = isBayesPriceSkill(s)
    const off = bayesPrice && Boolean(s.off_for_customer)
    const shadowed = bayesPrice ? s.shadowed_by : ''
    const dimmed = platform || off || Boolean(shadowed)
    const actions: MenuAction[] = [
      { title: 'View', icon: <VisibilityOutlinedIcon fontSize="small" />, onClick: () => onView(s) },
      // A built-in skill has no Install action: every chat already has it.
      ...(onInstall && !builtin ? [{
        title: 'Install in this chat', icon: <DownloadOutlinedIcon fontSize="small" />, onClick: () => onInstall(s),
        disabled: Boolean(reasons.install), reason: reasons.install,
      }] : []),
      ...(bayesPrice ? [
        {
          title: 'Duplicate to My skills', icon: <ContentCopyIcon fontSize="small" />, onClick: () => { void onDuplicate(s, 'yours').catch(() => undefined) },
          disabled: Boolean(reasons.duplicateOff), reason: reasons.duplicateOff,
        },
        {
          title: 'Duplicate to Company-level', icon: <ContentCopyIcon fontSize="small" />, onClick: () => { void onDuplicate(s, 'organisation').catch(() => undefined) },
          disabled: Boolean(reasons.duplicateToCompany), reason: reasons.duplicateToCompany,
        },
      ] : [{ title: 'Copy', icon: <ContentCopyIcon fontSize="small" />, onClick: () => onCopy(s) }]),
      { title: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => onEdit(s), disabled: Boolean(reasons.edit), reason: reasons.edit },
      // Only a Bayes Price Manager sees these; a customer's users never do.
      ...(canManageBayesPrice && bayesPrice ? [{
        title: off ? `Turn on for ${customer}` : `Turn off for ${customer}`, icon: <PowerSettingsNewIcon fontSize="small" />,
        onClick: () => { void onSetOff(s, !off).catch(() => undefined) },
      }] : []),
      ...(canManageBayesPrice && !platform && !bayesPrice ? [{
        title: 'Move to Bayes Price skills', icon: <PublicIcon fontSize="small" />, onClick: () => setMoving(s),
        disabled: Boolean(reasons.moveToBayesPrice), reason: reasons.moveToBayesPrice,
      }] : []),
      {
        title: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => setDeleting(s), color: 'error.main',
        disabled: Boolean(reasons.remove), reason: reasons.remove,
      },
    ]
    const owner = s.visibility === 'private' && !sameEmail(s.owner_email, currentUserEmail) ? s.owner_email : ''
    return (
      <ListItem
        key={s.id}
        data-testid={`skill-row-${s.id}`}
        disableGutters
        sx={{
          backgroundColor: 'background.paper', borderRadius: 1, mb: 0.5, px: 1, py: 0.75,
          alignItems: 'flex-start', gap: 0.5, opacity: off ? 0.55 : dimmed ? 0.72 : 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => onView(s)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{s.name}</Typography>
            {isDraft(s) && <Chip label="Draft" size="small" color="warning" variant="outlined" />}
            {builtin && (
              <Tooltip title="Every chat already has this skill">
                <Chip label="Built in" size="small" variant="outlined" />
              </Tooltip>
            )}
            {bayesPrice && <Chip label="Bayes Price" size="small" color="success" variant="outlined" />}
            {s.origin_newer && <Chip label={NEWER_LABEL} size="small" color="info" variant="outlined" />}
            {installed && onInstall && !builtin && <Chip label="Installed" size="small" color="success" variant="outlined" />}
          </Box>
          {owner && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{owner}</Typography>}
          {off && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>{OFF_LABEL}</Typography>}
          {shadowed && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{SHADOWED_LABEL[shadowed]}</Typography>}
          <Typography
            variant={dense ? 'caption' : 'body2'}
            color="text.secondary"
            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {s.description}
          </Typography>
          {bayesPrice && s.version_saved_at ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Version saved {formatSavedAt(s.version_saved_at)}</Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {onSaveDraft && isDraft(s) && sameEmail(s.owner_email, currentUserEmail) && (
            <Tooltip title={mine.some((m) => m.name === s.name) ? `Save to My skills. It replaces your saved skill ${s.name}.` : 'Save to My skills'}>
              <span>
              <Button
                size="small"
                variant="outlined"
                color="info"
                aria-label={`Save ${s.name} to My skills`}
                disabled={savingId === s.id}
                onClick={() => { void handleSaveDraft(s) }}
                sx={{ minWidth: 0, px: 1, py: 0.25, mr: 0.5 }}
              >
                Save
              </Button>
              </span>
            </Tooltip>
          )}
          {renderStar(s, reasons.promote)}
          <MenuActionsButton actions={actions} ariaLabel={`Actions for ${s.name}`} />
        </Box>
      </ListItem>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <TextField
        size="small"
        fullWidth
        placeholder="Search skills"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        slotProps={{
          input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> },
          htmlInput: { 'aria-label': 'Search skills' },
        }}
      />
      {loading ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>Loading skills…</Typography>
      ) : tiers.map((tier) => (
        <TierSection
          key={tier.variant}
          title={tier.title}
          count={tier.skills.length}
          open={isOpen(tier)}
          onToggle={() => toggle(tier)}
          variant={tier.variant}
        >
          {tier.skills.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1.5, px: 1 }}>{q ? 'No skills match' : tier.emptyLabel}</Typography>
          ) : (
            <List disablePadding>{tier.skills.map(renderRow)}</List>
          )}
        </TierSection>
      ))}

      <Dialog open={deleting !== null} onClose={() => setDeleting(null)}>
        <DialogTitle>{deleting && isDraft(deleting) ? 'Discard draft' : 'Delete skill'}</DialogTitle>
        <DialogContent>
          <Typography>
            {deleting && isDraft(deleting)
              ? `Discard the draft “${deleting.name}”? This cannot be undone.`
              : `Delete “${deleting?.name}”? The agent stops offering it. Chats that already installed it keep their copy. This cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="info" onClick={() => setDeleting(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>{deleting && isDraft(deleting) ? 'Discard' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={moving !== null} onClose={() => setMoving(null)}>
        <DialogTitle>Move to Bayes Price skills</DialogTitle>
        <DialogContent>
          <Typography>
            “{moving?.name}” leaves {moving?.visibility === 'organizational' ? 'Company-level' : 'My skills'} and becomes a Bayes Price skill. Every customer's chats get it, read-only, unless it is turned off for that customer or they have a skill of the same name.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="info" onClick={() => setMoving(null)}>Cancel</Button>
          <Button color="info" variant="contained" onClick={confirmMove}>Move</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={demoting !== null} onClose={() => setDemoting(null)}>
        <DialogTitle>Remove from Company-level</DialogTitle>
        <DialogContent>
          <Typography>
            “{demoting?.name}” goes back to its owner's skills. The agent stops using it for everyone else.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="info" onClick={() => setDemoting(null)}>Cancel</Button>
          <Button color="info" variant="contained" onClick={confirmDemote}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SkillsLibrary
